import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { TermsDialogComponent } from '../components/terms-dialog/terms-dialog.component';

/** Shown on the sign-in page after someone closes the Terms dialog without accepting. */
export const TERMS_DECLINED_NOTICE = 'You need to accept the Terms & Conditions to continue.';

/**
 * Terms & Conditions "gate" between a successful sign-in and the routing
 * that would normally follow it.
 *
 * The login page and the MFA challenge both land on an `AUTH_OK` response
 * that carries `termsAcceptedAt`. If the value is set, routing continues
 * as before. If it is null — the user has never accepted — we open the
 * dialog, block the trip to the workspace until they accept, and then
 * resume the exact routing the caller intended.
 *
 * Pulled out into one service because the alternative is identical code
 * in two components, with the usual risk that one of them drifts and
 * only half the sign-in paths end up prompting.
 */
@Injectable({ providedIn: 'root' })
export class TermsGateService {
  constructor(private dialog: MatDialog) {}

  /**
   * Call `routeNow(false)` immediately if the user has already accepted,
   * otherwise open the dialog and call `routeNow(true)` only after they do.
   * `afterPrompt` tells the caller time has passed since sign-in, so any
   * short-lived token in the sign-in response may have expired.
   * `onCancel` fires when the dialog closes without acceptance.
   */
  routeOrPrompt(
    termsAcceptedAt: string | null | undefined,
    routeNow: (afterPrompt: boolean) => void,
    onCancel: () => void,
  ): void {
    if (termsAcceptedAt) {
      routeNow(false);
      return;
    }
    this.dialog
      // Matches the Lab dialog's open config exactly: no custom panelClass,
      // no disableClose. The template renders its own full-screen overlay
      // so MatDialog's own panel chrome is painted over anyway — and so is
      // its backdrop, which is why the dialog handles the outside click
      // itself. Esc resolves `undefined` and an outside click `false`; the
      // gate treats either as a cancel and routes nowhere.
      .open(TermsDialogComponent)
      .afterClosed()
      .subscribe((accepted) => {
        if (accepted) {
          routeNow(true);
        } else {
          onCancel();
        }
      });
  }
}
